// src/pages/SelectRegularPaper.jsx
import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/selectregularpaper.css';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';

const API_BASE = 'https://digital.tifleducation.com/admin/public/api';
const PAPER_SAVED_KEY = 'regularPaperSaved';

const SelectRegularPaper = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Use a single source of truth for board/grade/subject
  const state = location.state || {};
  const storedSelection = JSON.parse(localStorage.getItem('regularPaperSelection') || '{}');

  const boardId = state.boardId || storedSelection.boardId;
  const gradeId = state.gradeId || storedSelection.gradeId;
  const subjectId = state.subjectId || storedSelection.subjectId;

  useEffect(() => {
    if (boardId && gradeId && subjectId) {
      localStorage.setItem(
        'regularPaperSelection',
        JSON.stringify({ boardId, gradeId, subjectId })
      );
    }
  }, [boardId, gradeId, subjectId]);

  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // ----- Form Data -----
  const getInitialFormData = () => {
    const paperSaved = JSON.parse(localStorage.getItem(PAPER_SAVED_KEY) || 'false');
    if (paperSaved) {
      localStorage.removeItem('regularPaperForm');
      localStorage.setItem(PAPER_SAVED_KEY, 'false');
      return {
        mcq: '', fill_in_the_blanks: '', true_false: '', very_short_qa: '',
        short_qa: '', long_qa: '', matching_type: '', diagram_image_based: ''
      };
    }

    const saved = JSON.parse(localStorage.getItem('regularPaperForm') || '{}');
    const counts = state.counts || {};
    return {
      mcq: counts.mcq ?? saved.mcq ?? '',
      fill_in_the_blanks: counts.fill_in_the_blanks ?? saved.fill_in_the_blanks ?? '',
      true_false: counts.true_false ?? saved.true_false ?? '',
      very_short_qa: counts.very_short_qa ?? saved.very_short_qa ?? '',
      short_qa: counts.short_qa ?? saved.short_qa ?? '',
      long_qa: counts.long_qa ?? saved.long_qa ?? '',
      matching_type: counts.matching_type ?? saved.matching_type ?? '',
      diagram_image_based: counts.diagram_image_based ?? saved.diagram_image_based ?? '',
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);

  // Persist formData to localStorage
  useEffect(() => {
    localStorage.setItem('regularPaperForm', JSON.stringify(formData));
  }, [formData]);

  // Validate board/grade/subject
  useEffect(() => {
    if (!boardId || !gradeId || !subjectId) {
      toast.error('Board/Grade/Subject missing. Please re-select from Papers.');
      navigate('/papers');
    }
  }, [boardId, gradeId, subjectId, navigate]);

  const handleChange = (e, key) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    const anyCount = Object.values(formData).some((v) => v && Number(v) > 0);
    if (!anyCount) {
      toast.error('Please enter at least one question count.');
      return;
    }

    localStorage.setItem('regularPaperForm', JSON.stringify(formData));
    navigate('/regularpaper', {
      state: { boardId, gradeId, subjectId, counts: formData },
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleDropdown = () => setShowDropdown((prev) => !prev);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ----- Dashboard & User Data -----
  const [dashboardData, setDashboardData] = useState({
    totalBoard: 0, totalGrade: 0, totalSubjects: 0, totalPapers: 0, totalQuestions: 0,
  });
  const [user, setUser] = useState({ id: null, name: '', email: '', password: '' });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE}/getDashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data) {
          setDashboardData({
            totalBoard: res.data.totalBoards || 0,
            totalGrade: res.data.totalGrades || 0,
            totalSubjects: res.data.totalSubjects || 0,
            totalPapers: res.data.totalPapers || 0,
            totalQuestions: res.data.totalQuestions || 0,
          });
          if (res.data.user) {
            setUser({
              id: res.data.user.id,
              name: res.data.user.name || '',
              email: res.data.user.email || '',
              password: '',
            });
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error.response?.data || error);
      }
    };

    fetchDashboardData();
  }, []);

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/update-profile`,
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profile updated successfully!');
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error('Error updating profile:', error.response?.data || error);
      toast.error('Failed to update profile');
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        <div className="select-header">
          <div className="papers-welcome-text">
            <a onClick={() => navigate('/papers', { state: { boardId, gradeId, subjectId } })} className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="select-welcome-text">Regular Paper - Enter Counts</span>
          </div>

          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className="edituser" onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={handleNext} className="question-form-container">
          <h3>Enter Number Of Questions</h3>
          <div className="question-grid">
            {[
              { key: 'mcq', label: 'M.C.Q ', placeholder: 'Enter M.C.Qs' },
              { key: 'fill_in_the_blanks', label: 'Fill in the blank', placeholder: 'Enter Fill in the blank' },
              { key: 'true_false', label: 'True - False', placeholder: 'Enter True - False' },
              { key: 'very_short_qa', label: 'Very Short Answer Questions', placeholder: 'Enter Very Short Answer Questions' },
              { key: 'short_qa', label: 'Short Answer Questions', placeholder: 'Enter Short Answer Questions' },
              { key: 'long_qa', label: 'Long Answer Questions/Essay Questions', placeholder: 'Enter Long Answer Questions' },
              { key: 'matching_type', label: 'Matching Type', placeholder: 'Enter Matching Type' },
              { key: 'diagram_image_based', label: 'Diagram-based/Image-based Questions', placeholder: 'Enter Diagram-based Questions' },
            ].map((item) => (
              <div key={item.key} className="form-item">
                <label><span className="dot">●</span> {item.label}</label>
                <input
                  type="text"
                  placeholder={item.placeholder}
                  value={formData[item.key] || ''}
                  onChange={(e) => handleChange(e, item.key)}
                />
              </div>
            ))}
          </div>

          <div className="next-button-container">
            <button type="submit" className="next-btn">Next</button>
          </div>
        </form>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>
            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={e => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={e => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ----- Modal Styles -----
const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};

export default SelectRegularPaper;
